import React from 'react';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { optimizeImage } from '../utils/cloudinary';

const BungeePage = () => {
  // Optimized images (no cropping, just compression + auto-format)
  const images = {
    smallGroup: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/adhbgarg_Bungee1_SmallGroupClasses.png', { width: 600 }),
    community: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/ki01ibaa_Bungee2_Liz%26Jenn_CommunityFitness.JPG', { width: 600 }),
    parties: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/7yjvhnwi_Bungee3_Parties%26Events.png', { width: 600 })
  };

  // YouTube video ID for embedding
  const bungeeVideoId = '2RmPq6q2I1k';

  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="service-hero-content">
          <div className="service-badge">Flight</div>
          <h1 className="service-page-title">Bungee Fitness</h1>
          <p className="service-page-subtitle">
            Cardio disguised as a really good time.
          </p>
        </div>
      </div>

      <div className="service-content">
        {/* Back to School Announcement Box */}
        <section className="seasonal-highlight bungee-summer">
          <div className="seasonal-badge">🔥 CLASSES RUNNING NOW</div>
          <h2 className="seasonal-title">Back to School</h2>
          <p className="seasonal-tagline">Time for you to focus on you.</p>
          
          <div className="seasonal-content">
            <div className="seasonal-video">
              <iframe
                src={`https://www.youtube.com/embed/${bungeeVideoId}?autoplay=1&mute=1&loop=1&playlist=${bungeeVideoId}&controls=0&rel=0&modestbranding=1&playsinline=1`}
                title="Bungee Fitness Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            </div>
            
            <div className="seasonal-details bungee-details">
              <div className="camp-info-grid">
                <div className="camp-info-item">
                  <span className="camp-label">Days</span>
                  <span className="camp-value">Thu · Fri · Sat · Sun</span>
                </div>
                <div className="camp-info-item">
                  <span className="camp-label">Times</span>
                  <span className="camp-value">8:30 AM · 10:00 AM · 11:30 AM</span>
                </div>
                <div className="camp-info-item">
                  <span className="camp-label">Drop-in</span>
                  <span className="camp-value">$35</span>
                </div>
                <div className="camp-info-item">
                  <span className="camp-label">New · Memberships</span>
                  <span className="camp-value">$150/mo<br />for 5 classes</span>
                </div>
              </div>
              
              <p className="camp-location">
                <strong>Location:</strong> The Studio inside Anytime Fitness, 5060 New Centre Dr, Ste 70 · Wilmington, NC 28403
              </p>
              
              <p className="bungee-disclaimer">
                Open to all fitness levels · Ages 18+ · Youth welcome at 90 lbs+ with an adult. <a href="https://www.gocalfit.com/faq#bungee" target="_blank" rel="noopener noreferrer">See FAQs</a>
              </p>
              
              <a 
                href="https://www.gocalfit.com/book-bungee" 
                target="_blank" 
                rel="noopener noreferrer"
                className="seasonal-cta"
              >
                <Button size="lg" className="register-btn">
                  Book Your Spot
                </Button>
              </a>
              
              <p className="spots-note">You'll be laughing too hard to notice you're working out. That's the point.</p>
            </div>
          </div>
        </section>

        {/* BOGO First-Timer Callout */}
        <section className="bogo-strip" data-testid="bungee-bogo-strip">
          <div className="bogo-strip-content">
            <span className="bogo-strip-badge">First-Timer Special</span>
            <h3 className="bogo-strip-heading">BOGO: Two Bungee 101 classes for $35</h3>
            <p className="bogo-strip-text">Try it once, come back free. That's how sure we are you'll love it.</p>
          </div>
          <Link to="/book-bungee" className="bogo-strip-cta-wrap">
            <Button size="lg" className="bogo-strip-cta" data-testid="bogo-claim-btn">
              Claim Your BOGO
            </Button>
          </Link>
        </section>

        {/* UPCOMING POP-UP EVENTS — each button auto-hides after its event */}
        {(new Date() < new Date('2026-10-23T18:30:00') || new Date() < new Date('2026-10-30T19:00:00')) && (
          <section className="popup-events-strip" data-testid="bungee-popup-events-strip">
            <h3 className="popup-events-title">Upcoming Pop-Up Events</h3>
            <div className="popup-events-buttons">
              {new Date() < new Date('2026-10-23T18:30:00') && (
                <Link to="/book-event" className="popup-event-btn" data-testid="bungee-popup-event-trapeze">
                  <span className="popup-event-btn-label">Adult Trapeze Pop-up</span>
                  <span className="popup-event-btn-note">Thu, Oct 23 · 6:30pm</span>
                </Link>
              )}
              {new Date() < new Date('2026-10-30T19:00:00') && (
                <Link to="/book-event" className="popup-event-btn halloween" data-testid="bungee-popup-event-glow">
                  <span className="popup-event-btn-label">🎃 Bungee HALLOWEEN Glow Party</span>
                  <span className="popup-event-btn-note">Fri, Oct 30 · 5:30 & 7:00pm</span>
                </Link>
              )}
            </div>
          </section>
        )}

        <section className="service-description-section">
          <h2>What It Actually Feels Like</h2>
          <p>
            Bungee fitness combines cardio, resistance training and core work in a way that's genuinely easy on your joints — and genuinely hard on your muscles. Suspended by a bungee cord, you'll move in ways you didn't know your body could. Low impact. High cardio. Nothing like anything you've tried before.
          </p>
          <p className="service-tagline-italic">Perfect for adults ready to reclaim their strength and freedom.</p>
          <p className="service-affiliate-note">
            We are a proud affiliate of <strong>SLING Bungee</strong>. We use SLING's certified equipment and teach their official class format, so you get a safe, authentic, high-quality bungee experience every time.
          </p>
        </section>

        <section className="service-features">
          <Link to="/book-bungee" className="feature-card-link">
            <div className="feature-card">
              <img 
                src={images.smallGroup}
                alt="Small Group Classes"
                className="feature-card-image"
              />
              <div className="feature-card-content">
                <h3>Small Group Classes</h3>
                <p>High-cardio training. Low-impact landings. Addictive results.</p>
              </div>
            </div>
          </Link>
          <Link to="/book-bungee" className="feature-card-link">
            <div className="feature-card">
              <img 
                src={images.community}
                alt="Community Fitness"
                className="feature-card-image"
              />
              <div className="feature-card-content">
                <h3>Community Fitness</h3>
                <p>Move together. Stay together. Fueled by fun and real people.</p>
              </div>
            </div>
          </Link>
          <Link to="/book-bungee" className="feature-card-link">
            <div className="feature-card">
              <img 
                src={images.parties}
                alt="Parties & Events"
                className="feature-card-image"
              />
              <div className="feature-card-content">
                <h3>Parties & Events</h3>
                <p>Group bungeeing. Your next party just got a whole lot more interesting.</p>
              </div>
            </div>
          </Link>
        </section>

        <section className="scheduling-section">
          <h2>Ready to Fly?</h2>
          <p>Your first class is waiting.</p>
          
          <div className="registration-cta-container">
            <Link to="/book-bungee">
              <Button 
                size="lg"
                className="registration-popup-btn"
                data-testid="bungee-schedule-btn"
              >
                <Calendar size={20} />
                Book a Class
              </Button>
            </Link>
          </div>
          
          <div className="faq-redirect">
            <p>First time? We've got you covered. <Link to="/faq" className="faq-link">Read the FAQs →</Link></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BungeePage;
